import type { SxProps, Theme } from "@mui/material";
import { Button } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

type Props = {
  to: string;
  includes: string | string[];
  icon?: ReactNode;
  sx?: SxProps<Theme>;
};

function useIsActive(includes: string | string[]) {
  const location = useLocation();
  return useMemo(
    () =>
      typeof includes === "string"
        ? location.pathname.includes(includes)
        : includes.some((include) => location.pathname.includes(include)),
    [location.pathname, includes]
  );
}

export function MainHeaderButton({ to, includes, children, icon }: PropsWithChildren<Props>) {
  const isActive = useIsActive(includes);

  return (
    <Button
      variant={isActive ? "text" : "outlined"}
      color={isActive ? "secondary" : "inherit"}
      component={Link}
      size="small"
      to={to}
      sx={{
        border: 0,
      }}
      startIcon={icon}
    >
      {children}
    </Button>
  );
}

export const NavigationButton = React.memo(
  ({
    to,
    includes,
    children,
    icon,
    sx,
    color = "secondary",
    variant = "outlined",
  }: PropsWithChildren<Props & { color?: string; variant?: "outlined" | "contained" }>) => {
    const isActive = useIsActive(includes);

    return (
      <Button
        variant={isActive ? "contained" : variant}
        color={
          color as "secondary" | "inherit" | "primary" | "success" | "error" | "info" | "warning"
        }
        component={Link}
        size="small"
        to={to}
        startIcon={icon}
        sx={sx}
      >
        {children}
      </Button>
    );
  }
);
